import {TransactionGroupBy} from '@sollapay/enums';
import {SearchInput} from '@sollapay/ui';
import {ContentSection, ListEmptySearch} from '@sollapay/ui/components';
import {useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {useTrustAccountTransactionsQuery} from '@/features/trust-accounts/hooks';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';

import {DEFAULT_TRANSACTIONS_FILTER_STATE} from './config/transactions-filter.config';
import {useTransactionsUrlFilter} from './hooks';
import {TransactionsEmptyState} from './TransactionsEmptyState';
import {TransactionsFilter} from './TransactionsFilter';
import {TransactionsGroupBySelector} from './TransactionsGroupBySelector';
import {TransactionsTabContentSkeleton} from './TransactionsTabContent.skeleton';
import {TransactionsTimelineTable} from './TransactionsTimelineTable';
import {
  buildSearchableTransactions,
  filterTransactions,
  groupTransactions
} from './utils/transactions.utils';

import type {FC} from 'react';

export const TransactionsTabContent: FC = () => {
  const {id: trustAccountId} = useCurrentTrustAccount();
  const {t} = useTranslation('trustAccounts');
  const [groupBy, setGroupBy] = useState<TransactionGroupBy>(TransactionGroupBy.DATE);
  const {filter, setFilter} = useTransactionsUrlFilter();
  const [search, setSearch] = useState('');

  const transactionsQuery = useTrustAccountTransactionsQuery(trustAccountId);

  const allTransactions = useMemo(
    () => transactionsQuery.data?.data ?? [],
    [transactionsQuery.data]
  );

  // Lowercases each transaction's searchable fields once — not on every search keystroke.
  const searchableTransactions = useMemo(
    () => buildSearchableTransactions(allTransactions),
    [allTransactions]
  );

  const filteredGroups = useMemo(
    () => groupTransactions(filterTransactions(searchableTransactions, filter, search), groupBy),
    [searchableTransactions, filter, search, groupBy]
  );

  if (transactionsQuery.isLoading) {
    return <TransactionsTabContentSkeleton />;
  }

  if (allTransactions.length === 0) {
    return <TransactionsEmptyState />;
  }

  return (
    <ContentSection
      title={t('view.transactions.sectionTitle')}
      subtitle={t('view.transactions.sectionSubtitle')}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('view.transactions.search.placeholder')}
          />
          <div className="flex items-center gap-2">
            <TransactionsGroupBySelector value={groupBy} onChange={setGroupBy} />
            <TransactionsFilter filter={filter} onChange={setFilter} />
          </div>
        </div>

        <TransactionsTimelineTable
          groups={filteredGroups}
          groupBy={groupBy}
          emptyState={
            <ListEmptySearch
              title={t('view.transactions.emptySearch.title')}
              subtitle={t('view.transactions.emptySearch.subtitle')}
              clearLabel={t('view.transactions.emptySearch.clear')}
              onClear={() => {
                setSearch('');
                setFilter(DEFAULT_TRANSACTIONS_FILTER_STATE);
              }}
            />
          }
        />
      </div>
    </ContentSection>
  );
};
